import React from 'react';
import { Card, Tag, Divider, Row, Col, Progress, Space, Typography } from 'antd';
import { 
  UserOutlined, 
  MessageOutlined, 
  ThunderboltOutlined, 
  TeamOutlined, 
  TagsOutlined, 
  DeploymentUnitOutlined, 
  SecurityScanOutlined, 
  RocketOutlined,
  CheckCircleOutlined,
  QuestionCircleOutlined,
  PictureOutlined
} from '@ant-design/icons';

const { Text, Title, Paragraph } = Typography;

const AgentBlueprintView = ({ config, stages, faqs, title, subTitle }) => {
  if (!config) return null;

  const renderSectionHeader = (icon, title, extra) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, marginTop: 8 }}>
      <Space size="middle">
        <span style={{ color: '#8c8c8c', fontSize: 16, display: 'flex' }}>{icon}</span>
        <Text strong style={{ fontSize: 12, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {title}
        </Text>
      </Space>
      {extra}
    </div>
  );

  return (
    <div className="agent-blueprint-view" style={{ padding: '0' }}>
      <div style={{ marginBottom: 20, borderBottom: '1px solid #f0f0f0', paddingBottom: 10 }}>
        <Title level={5} style={{ marginBottom: 2, fontSize: 16, fontWeight: 700, color: '#1a1a1a' }}>
          {title || "Bản Phác Thảo Đang Hoạt Động"}
        </Title>
        <Text type="secondary" style={{ fontSize: 11, color: '#8c8c8c' }}>
          {subTitle || `Cấu hình hiện tại của ${config.agentName || 'Agent'}.`}
        </Text>
      </div>

      {/* IDENTITY */}
      <div style={{ marginBottom: 20 }}>
        {renderSectionHeader(<UserOutlined style={{ fontSize: 14 }} />, "Định Danh", <Tag color="blue" style={{ borderRadius: '4px', fontSize: 9, fontWeight: 700, border: 'none', backgroundColor: '#e6f7ff', color: '#1890ff', height: '18px', lineHeight: '18px' }}>ỔN ĐỊNH</Tag>)}
        <div style={{ padding: '8px 0' }}>
          <Row style={{ marginBottom: 8 }}>
            <Col span={8}><Text style={{ color: '#8c8c8c', fontSize: 12 }}>Tên</Text></Col>
            <Col span={16} style={{ textAlign: 'right' }}><Text strong style={{ fontSize: 13, color: '#1a1a1a' }}>{config.agentName || 'Chưa đặt tên'}</Text></Col>
          </Row>
          <Row style={{ marginBottom: 8 }}>
            <Col span={8}><Text style={{ color: '#8c8c8c', fontSize: 12 }}>Vai trò</Text></Col>
            <Col span={16} style={{ textAlign: 'right' }}>
              <Tag color="blue" style={{ margin: 0, borderRadius: '4px', fontWeight: 600, fontSize: 11 }}>{config.agentRole || 'Trợ lý bán hàng'}</Tag>
            </Col>
          </Row>
          <Row style={{ marginBottom: 8 }}>
            <Col span={10}><Text style={{ color: '#8c8c8c', fontSize: 12 }}>Xử lý hình ảnh</Text></Col>
            <Col span={14} style={{ textAlign: 'right' }}>
              <Tag 
                icon={config.visionEnabled ? <CheckCircleOutlined /> : <PictureOutlined />}
                color={config.visionEnabled ? "success" : "default"}
                style={{ margin: 0, borderRadius: '4px', fontWeight: 600, fontSize: 11 }}
              >
                {config.visionEnabled ? 'ĐÃ BẬT' : 'CHƯA HỖ TRỢ'}
              </Tag>
            </Col>
          </Row>
          <Row>
            <Col span={8}><Text style={{ color: '#8c8c8c', fontSize: 12 }}>Lời chào</Text></Col>
            <Col span={16} style={{ textAlign: 'right' }}>
              <Text italic style={{ fontSize: 11, color: '#595959' }}>"{config.greetingMessage || 'Xin chào...'}"</Text>
            </Col>
          </Row>
        </div>
      </div>

      {/* SYSTEM PROMPT */}
      <div style={{ marginBottom: 20 }}>
        {renderSectionHeader(<MessageOutlined style={{ fontSize: 14 }} />, "Chỉ dẫn hệ thống")}
        <div style={{ 
          padding: '12px', 
          borderRadius: '8px', 
          backgroundColor: '#f9fafb',
          border: '1px solid #f0f0f0',
          fontSize: '12px',
          lineHeight: '1.5',
          color: '#434343',
          whiteSpace: 'pre-wrap'
        }}>
          {config.systemPrompt || 'Bạn là trợ lý ảo chuyên nghiệp...'}
        </div>
      </div>

      {/* PERSONALITY */}
      <div style={{ marginBottom: 20 }}>
        {renderSectionHeader(<ThunderboltOutlined style={{ fontSize: 14 }} />, "Nhân cách (Personality)")}
        <div style={{ 
          padding: '12px', 
          borderRadius: '8px', 
          backgroundColor: '#f0f7ff',
          borderLeft: '3px solid #1890ff',
          fontSize: '12px',
          lineHeight: '1.5',
          color: '#003a8c',
          fontStyle: 'italic'
        }}>
          {config.agentPersonality || 'Chưa có mô tả nhân cách.'}
        </div>
      </div>

      {/* RESPONSE STYLE */}
      <div style={{ marginBottom: 20 }}>
        {renderSectionHeader(<TeamOutlined style={{ fontSize: 14 }} />, "Phong cách phản hồi")}
        <div style={{ padding: '8px 0' }}>
          <Row gutter={24}>
            <Col span={8}>
              <div style={{ marginBottom: 4 }}>
                <Text style={{ fontSize: 10, color: '#8c8c8c', textTransform: 'uppercase', fontWeight: 600 }}>Độ dài phản hồi</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Progress percent={config.style?.response_length || 50} size="small" strokeColor="#1890ff" showInfo={false} style={{ flex: 1 }} />
                <Text strong style={{ fontSize: 11, color: '#1890ff' }}>{config.style?.response_length || 50}%</Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ marginBottom: 4 }}>
                <Text style={{ fontSize: 10, color: '#8c8c8c', textTransform: 'uppercase', fontWeight: 600 }}>Sử dụng Emoji</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Progress percent={config.style?.emoji_level || 50} size="small" strokeColor="#52c41a" showInfo={false} style={{ flex: 1 }} />
                <Text strong style={{ fontSize: 11, color: '#52c41a' }}>{config.style?.emoji_level || 50}%</Text>
              </div>
            </Col>
            <Col span={8}>
              <div style={{ marginBottom: 4 }}>
                <Text style={{ fontSize: 10, color: '#8c8c8c', textTransform: 'uppercase', fontWeight: 600 }}>Mức độ trang trọng</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Progress percent={config.style?.formality_level || 50} size="small" strokeColor="#fa8c16" showInfo={false} style={{ flex: 1 }} />
                <Text strong style={{ fontSize: 11, color: '#fa8c16' }}>{config.style?.formality_level || 50}%</Text>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* GENERAL STRATEGY */}
      <div style={{ marginBottom: 20 }}>
        {renderSectionHeader(<DeploymentUnitOutlined style={{ fontSize: 14 }} />, "Chiến lược chung")}
        <div style={{ padding: '8px 0' }}>
          <div style={{ marginBottom: 12 }}>
            <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Tên chiến lược</Text>
            <Tag color="purple" style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: '4px' }}>
              {config.strategy?.name || 'CHƯA ĐẶT TÊN'}
            </Tag>
          </div>

          <Row gutter={16}>
            <Col span={12}>
              <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Nhiệm vụ cốt lõi</Text>
              <div style={{ 
                padding: '8px 12px', 
                borderRadius: '6px', 
                backgroundColor: '#fafafa', 
                border: '1px solid #f0f0f0',
                fontSize: 11,
                minHeight: '60px',
                color: '#595959'
              }}>
                {config.strategy?.objection_handling_guide || 'Chưa có nhiệm vụ.'}
              </div>
            </Col>
            <Col span={12}>
              <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>Giọng điệu giao tiếp</Text>
              <div style={{ 
                padding: '8px 12px', 
                borderRadius: '6px', 
                backgroundColor: '#fafafa', 
                border: '1px solid #f0f0f0',
                fontSize: 11,
                minHeight: '60px',
                color: '#595959'
              }}>
                {config.strategy?.tone || 'Chưa có giọng điệu.'}
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {/* VOCABULARY */}
      <div style={{ marginBottom: 20 }}>
        {renderSectionHeader(<TagsOutlined style={{ fontSize: 14 }} />, "Từ vựng then chốt")}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
          {(config.strategy?.key_phrases || []).length > 0 ? config.strategy.key_phrases.map((phrase, idx) => (
            <Tag key={idx} style={{ 
              borderRadius: '12px', 
              padding: '0 10px', 
              backgroundColor: '#fff', 
              border: '1px solid #d9d9d9', 
              color: '#595959',
              fontSize: 11,
              margin: 0
            }}>{phrase}</Tag>
          )) : <Text type="secondary" style={{ fontSize: 11 }}>Chưa có từ khóa.</Text>}
        </div>
      </div>

      {/* SALES PIPELINE */}
      <div style={{ marginBottom: 20 }}>
        {renderSectionHeader(<DeploymentUnitOutlined style={{ fontSize: 14 }} />, "Quy trình bán hàng")}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {(stages && stages.length > 0 ? stages : []).sort((a,b) => a.sequenceOrder - b.sequenceOrder).map((stage, idx) => (
            <div key={idx} style={{ 
              padding: '10px 12px', 
              borderRadius: '8px', 
              border: '1px solid #f0f0f0', 
              backgroundColor: '#fff',
              display: 'flex',
              gap: 12,
              alignItems: 'center'
            }}>
              <div style={{ 
                width: 20, height: 20, borderRadius: '50%', 
                backgroundColor: '#1890ff', 
                color: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700, flexShrink: 0
              }}>
                {idx + 1}
              </div>
              <div style={{ flex: 1 }}>
                <Text strong style={{ fontSize: 12, color: '#1a1a1a', display: 'block' }}>{stage.displayName || stage.name}</Text>
                <div style={{ fontSize: 11, color: '#8c8c8c', lineHeight: 1.2 }}>{stage.goal}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ KNOWLEDGE */}
      <div style={{ marginBottom: 20 }}>
        {renderSectionHeader(<QuestionCircleOutlined style={{ fontSize: 14 }} />, "Kiến thức (FAQs)")}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(faqs && faqs.length > 0 ? faqs : []).slice(0, 3).map((faq, idx) => (
            <div key={idx} style={{ 
              padding: '8px 12px', 
              borderRadius: '6px', 
              backgroundColor: '#fafafa', 
              border: '1px solid #f0f0f0',
              fontSize: 11
            }}>
              <Text strong style={{ color: '#595959', display: 'block' }}>Q: {faq.question}</Text>
              <Text type="secondary" style={{ display: 'block', marginTop: 2 }}>A: {faq.answer?.substring(0, 80)}...</Text>
            </div>
          ))}
          {faqs && faqs.length > 3 && (
            <Text type="secondary" style={{ fontSize: 10, textAlign: 'center', marginTop: 4 }}>
              ... và {faqs.length - 3} câu hỏi khác
            </Text>
          )}
          {(!faqs || faqs.length === 0) && <Text type="secondary" style={{ fontSize: 11 }}>Chưa có câu hỏi tùy chỉnh.</Text>}
        </div>
      </div>
    </div>
  );
};

export default AgentBlueprintView;
